export type ParkingUtilizationPoint = {
  hour: number | string;
  occupied: number | string;
};

const HOURS_IN_DAY = 24;

export const normalizeParkingIdentifier = (parkingId: string | number | null | undefined): string | null => {
  if (typeof parkingId === 'string') {
    const trimmed = parkingId.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof parkingId === 'number' && Number.isFinite(parkingId)) {
    return String(parkingId);
  }
  return null;
};

export const normalizeParkingUtilizationResponse = (payload: unknown): number[] => {
  const chart = Array.from({ length: HOURS_IN_DAY }, () => 0);
  const entries = extractUtilizationArray(payload);
  if (!entries) {
    return chart;
  }

  for (const entry of entries) {
    const parsedEntry = parseUtilizationPoint(entry);
    if (!parsedEntry) {
      continue;
    }
    const { hourIndex, occupied } = parsedEntry;
    chart[hourIndex] = Math.max(chart[hourIndex], occupied);
  }

  return chart;
};

const extractUtilizationArray = (payload: unknown, depth = 0): unknown[] | null => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== 'object' || depth > 3) {
    return null;
  }
  const record = payload as Record<string, unknown>;
  const candidates = [record._data_, record['_data_'], record.data, record.body, record.response];

  for (const candidate of candidates) {
    if (!candidate || candidate === payload) {
      continue;
    }
    const extracted = extractUtilizationArray(candidate, depth + 1);
    if (extracted) {
      return extracted;
    }
  }

  return null;
};

const parseUtilizationPoint = (value: unknown): { hourIndex: number; occupied: number } | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const record = value as Record<string, unknown>;
  const hourIndex = parseHourIndex(record.hour ?? record.Hour ?? record.h);
  const occupied = parsePercentage(record.occupied ?? record.Occupied ?? record.value ?? record.percentage);

  if (hourIndex === null || occupied === null) {
    return null;
  }

  return { hourIndex, occupied };
};

const parseHourIndex = (value: unknown): number | null => {
  const numeric = parseNumber(value);
  if (numeric === null) {
    return null;
  }
  // API zwraca godziny od 1 do 24; konwertujemy na indeks 0-23
  if (numeric >= 1 && numeric <= HOURS_IN_DAY) {
    return numeric - 1;
  }
  if (numeric === 0) {
    return 0;
  }
  return null;
};

const parsePercentage = (value: unknown): number | null => {
  const numeric = parseNumber(value);
  if (numeric === null || numeric < 0) {
    return null;
  }
  const clamped = Math.min(100, numeric);
  return Number.isFinite(clamped) ? Math.round(clamped) : null;
};

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized.length) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};
